import OptimizedImage from "@/components/OptimizedImage";
import React from "react";
import { User } from "lucide-react";
import AchievmentRoad from "./AchievmentRoad";
import HeroFrame from "./HeroFrame";
import AchievmentMobileRoad from "./AchievmentMobileRoad";
import { useArtistUsp } from "@/api/hooks/useArtistUsp";
import { useArtistDisplayName, useArtistProfilePic } from "@/api/hooks/useArtistDisplayName";

const ArtistEliteHero = () => {
  const { uspText, hasUsp } = useArtistUsp();
  const displayName = useArtistDisplayName();
  const profilePic = useArtistProfilePic();
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Discover Tom Cruise – A Hollywood Icon!",
          text: `From "Top Gun" to "Mission: Impossible," Tom Cruise has defined action cinema for decades. A Hollywood legend with multiple accolades, he continues to thrill audiences worldwide. Check out his incredible journey here!`,
          url: `${window.location.origin}/tom-cruise`,
        });
      } else {
        alert(
          "Your browser does not support the Web Share API. Please use another browser."
        );
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  return (
    <div className="pt-5 md:pt-12 w-screen">

      <div className="flex items-center max-md:items-start justify-around max-md:justify-between max-md:flex-col min-h-[75vh]">
        <div className="space-y-4 p-10 max-sm:p-0 rounded-3xl max-sm:px-4">
          <div>
            <p className="text-xl max-sm:text-sm flex items-end gap-4">
              <svg
                width="50"
                height="50"
                viewBox="0 0 70 50"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M62.8238 49.9205L69.1641 10.4293L46.7306 21.0456L34.5816 0L22.4298 21.0456L-0.000762939 10.4293L6.30855 49.9753L62.8238 49.9205Z"
                  fill="#893B0C"
                />
                <path
                  d="M0.851959 11.5654L22.699 21.9033L34.5822 1.32031L46.464 21.9033L68.3125 11.5654L62.2591 49.2596L6.8744 49.3144L0.851959 11.5654Z"
                  fill="url(#paint0_linear_1134_5763)"
                />
                <path
                  d="M58.2924 44.6003L62.3125 19.5642L44.5812 27.9546L34.5892 10.6465L24.5945 27.9546L6.85329 19.5571L10.8551 44.6496L58.2924 44.6003Z"
                  fill="url(#paint1_linear_1134_5763)"
                />
                <path
                  d="M58.2897 44.6011L62.2578 49.2596L6.8731 49.3144L10.8524 44.6503L58.2897 44.6011Z"
                  fill="url(#paint2_linear_1134_5763)"
                />
                <path
                  d="M46.4609 21.9028L44.5767 27.9548L34.5848 10.6467L34.5791 1.31982L46.4609 21.9028Z"
                  fill="#E8F983"
                />
                <path
                  d="M34.5803 1.32031L34.5859 10.6472L24.5912 27.9553L22.6971 21.9033L34.5803 1.32031Z"
                  fill="url(#paint3_linear_1134_5763)"
                />
                <path
                  d="M0.849783 11.5654L6.84974 19.5579L10.8516 44.6503L6.87223 49.3144L0.849783 11.5654Z"
                  fill="url(#paint4_linear_1134_5763)"
                />
                <g opacity="0.9">
                  <path
                    d="M6.85112 19.5581C6.85112 19.5581 7.00157 21.1414 7.29967 23.5065C7.46138 24.6877 7.62168 26.0685 7.84525 27.5407C8.06602 29.0129 8.30225 30.5836 8.53708 32.1542C8.80143 33.7179 9.06716 35.2857 9.31464 36.7537C9.56071 38.2217 9.83773 39.5856 10.0501 40.7583C10.5014 43.0953 10.8516 44.6505 10.8516 44.6505C10.8516 44.6505 10.7025 43.0644 10.403 40.7007C10.2413 39.521 10.0824 38.1401 9.85601 36.6679C9.63665 35.1957 9.399 33.625 9.16277 32.053C8.89842 30.488 8.63406 28.9229 8.38658 27.4535C8.1391 25.9855 7.86352 24.6216 7.64979 23.4489C7.19983 21.1119 6.85112 19.5581 6.85112 19.5581Z"
                    fill="white"
                  />
                </g>
                <path
                  d="M68.3125 11.5654L62.3111 19.5649L44.5798 27.9553L46.464 21.9033L68.3125 11.5654Z"
                  fill="url(#paint5_linear_1134_5763)"
                />
                <path
                  d="M4.86152 33.0125C6.20296 40.2611 9.08133 45.8167 11.2819 45.4132C13.4896 45.0026 14.1856 38.7861 12.8329 31.529C11.4858 24.2692 8.61309 18.729 6.40968 19.1396C4.20487 19.5446 3.51303 25.7597 4.86152 33.0125Z"
                  fill="url(#paint6_radial_1134_5763)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_1134_5763"
                    x1="72.0673"
                    y1="20.8515"
                    x2="-20.731"
                    y2="38.271"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_1134_5763"
                    x1="78.964"
                    y1="-2.82834"
                    x2="-13.9634"
                    y2="67.0945"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint2_linear_1134_5763"
                    x1="131.994"
                    y1="-69.5351"
                    x2="43.7879"
                    y2="38.0397"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint3_linear_1134_5763"
                    x1="31.4116"
                    y1="9.69186"
                    x2="12.4176"
                    y2="54.8259"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint4_linear_1134_5763"
                    x1="29.4712"
                    y1="19.5576"
                    x2="-15.5048"
                    y2="38.6679"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint5_linear_1134_5763"
                    x1="79.4668"
                    y1="7.05133"
                    x2="45.6091"
                    y2="25.5237"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <radialGradient
                    id="paint6_radial_1134_5763"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(8.85283 32.4634) rotate(169.467) scale(3.55628 12.2919)"
                  >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0" />
                  </radialGradient>
                </defs>
              </svg>
              {/* CELEBRITY APPEARANCE */}
            </p>
            <div className="flex flex-row justify-between items-center">
              <h1 className="text-5xl max-w-xs max-sm:text-2xl font-bold">
                {displayName}
              </h1>
              <div className=" z-40">
                <div className="flex flex-col justify-center items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="size-6 cursor-pointer hover:scale-[1.2] transition-all duration-300"

                  >
                    <path
                      fillRule="evenodd"
                      d="M15.75 4.5a3 3 0 1 1 .825 2.066l-8.421 4.679a3.002 3.002 0 0 1 0 1.51l8.421 4.679a3 3 0 1 1-.729 1.31l-8.421-4.678a3 3 0 1 1 0-4.132l8.421-4.679a3 3 0 0 1-.096-.755Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>

          </div>

          <p
            className={`max-w-lg max-md:max-w-sm max-sm:text-sm text-lg font-extralight opacity-80 ${!hasUsp ? "italic" : ""}`}
          >
            {uspText}
          </p>

          <button className="bg-gradient-to-b from-[#EC2179] to-[#F16B2E] max-sm:text-base px-8 py-3 text-lg rounded-xl">
            SEE PRICE & BOOK
          </button>
        </div>



        <div className="w-[27rem] max-md:w-full h-[600px] relative">
          <div className="bg-[#2C2C2E] w-full max-md:w-[80%] max-md:ml-20 max-sm:w-full max-sm:ml-0 absolute h-full z-10 mt-8 flex max-sm:justify-center items-center">
            {profilePic ? (
              <OptimizedImage
                src={profilePic}
                alt={displayName}
                fill={true}
                loading="lazy"
                unoptimized={true}
                fetchPriority="high"
                className="object-cover object-top"
              />
            ) : (
              <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
            )}
          </div>
          <div className="absolute top-0 right-2 w-full max-md:w-[80%] max-sm:w-full h-full flex items-center justify-center z-0">
            <HeroFrame />
          </div>
        </div>

      </div>
      <AchievmentRoad />
      <AchievmentMobileRoad />
    </div>

  );
};

export default ArtistEliteHero;
